// «Написать разработчикам» в «Настройках»: проблема, идея или вопрос — админам в центр уведомлений, push и на почту
// (data_service/services/feedback.py). Без почты (вход через Telegram) — спрашиваем, как связаться.
// Черновик живёт, пока открыта страница: закрыли окно — текст остался; после отправки — очищается.
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ResponsiveDialog from './ResponsiveDialog';
import { ActionRow, ChoiceList, DialogHeader, SectionTitle, StatusText, ToggleRow } from './settings/SettingsControls';
import { FormField } from './form/FormFields';
import useKeyboardInset from './form/useKeyboardInset';
import { useDesktopLayout } from './layout/AppLayout';
import { primaryButtonSx, secondaryButtonSx } from './ServerForm';
import { sendFeedback } from '../services/dataService';
import { extractErrorMessage } from '../utils/errors';
import { isTelegramMiniApp } from '../utils/platform';
import { isStandalone } from '../utils/device';
import {
  CONTACT_MAX,
  FEEDBACK_TYPES,
  TEXT_MAX,
  collectContext,
  contactPrefill,
  contextPreview,
  successText,
  textError,
} from '../utils/feedback';
import { MUTED_TEXT_COLOR } from '../styles/tokens';

const emptyDraft = (prefill) => ({ type: 'problem', text: '', contact: prefill ?? '', attach: true });

export default function FeedbackCard({ token, email, username, buildVersion, serverName, sx }) {
  const prefill = contactPrefill({ email, username });
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => emptyDraft(prefill));
  const [sent, setSent] = useState(null); // текст «Спасибо…» после отправки

  // Профиль догрузился позже — ник в пустое поле контакта
  useEffect(() => {
    if (prefill) setDraft((prev) => (prev.contact ? prev : { ...prev, contact: prefill }));
  }, [prefill]);

  const openDialog = () => {
    setSent(null);
    setOpen(true);
  };

  return (
    <Box sx={sx}>
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Обратная связь</Typography>
      <ActionRow
        first
        icon={ChatBubbleOutlineIcon}
        label="Написать разработчикам"
        hint="Проблема, идея или вопрос"
        onClick={openDialog}
      />
      <FeedbackDialog
        open={open}
        onClose={() => setOpen(false)}
        token={token}
        askContact={prefill !== null}
        draft={draft}
        setDraft={setDraft}
        sent={sent}
        onSent={(text) => {
          setSent(text);
          setDraft(emptyDraft(prefill));
        }}
        buildVersion={buildVersion}
        serverName={serverName}
      />
    </Box>
  );
}

function FeedbackDialog({ open, onClose, token, askContact, draft, setDraft, sent, onSent, buildVersion, serverName }) {
  const desktop = useDesktopLayout();
  const keyboardInset = useKeyboardInset(open && !desktop);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [touched, setTouched] = useState(false);

  const context = useMemo(
    () =>
      collectContext({
        version: buildVersion,
        telegram: isTelegramMiniApp(),
        standalone: isStandalone(),
        userAgent: navigator.userAgent,
        screen: window.screen,
      }),
    [buildVersion]
  );

  const error = textError(draft.text);
  const update = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
  const close = () => {
    if (busy) return;
    setStatus(null);
    setTouched(false);
    onClose();
  };

  const submit = async (event) => {
    event.preventDefault();
    setTouched(true);
    if (error) return;
    setBusy(true);
    setStatus(null);
    try {
      const result = await sendFeedback(token, {
        type: draft.type,
        text: draft.text.trim(),
        contact: askContact ? draft.contact.trim() || null : null,
        context: draft.attach ? context : null,
      });
      setTouched(false);
      onSent(successText(result?.reply_via));
    } catch (err) {
      setStatus({ type: 'error', text: extractErrorMessage(err) || 'Не удалось отправить. Попробуйте позже.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={close} labelledBy="feedback-title" keyboardInset={keyboardInset}>
      {sent ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DialogHeader id="feedback-title" title="Отправлено" onClose={close} />
          <Typography role="status" sx={{ fontSize: '15px', color: '#e4e4e7' }}>{sent}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={close} sx={primaryButtonSx}>Готово</Button>
          </Box>
        </Box>
      ) : (
        <Box component="form" noValidate onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DialogHeader id="feedback-title" title="Написать разработчикам" onClose={close} disabled={busy} />

          <Box>
            <SectionTitle sx={{ mt: 0 }}>Что случилось</SectionTitle>
            <ChoiceList label="Тип сообщения" options={FEEDBACK_TYPES} value={draft.type} onChange={(type) => update({ type })} disabled={busy} />
          </Box>

          <FormField
            id="feedback-text"
            label="Сообщение"
            multiline
            minRows={4}
            maxRows={12}
            maxLength={TEXT_MAX}
            value={draft.text}
            onChange={(e) => update({ text: e.target.value })}
            onBlur={() => setTouched(true)}
            disabled={busy}
            placeholder={draft.type === 'problem' ? 'Что делали, что ожидали увидеть и что получилось' : ''}
            error={touched && Boolean(error)}
            hint={touched && error ? error : `${draft.text.trim().length} / ${TEXT_MAX}`}
          />

          {askContact && (
            <FormField
              id="feedback-contact"
              label="Как с вами связаться (необязательно)"
              maxLength={CONTACT_MAX}
              value={draft.contact}
              onChange={(e) => update({ contact: e.target.value })}
              disabled={busy}
              placeholder="@ник в Telegram или телефон"
              hint="Почты в профиле нет — напишем сюда, если понадобится уточнить"
            />
          )}

          <ToggleRow
            id="feedback-attach"
            first
            label="Приложить технические данные"
            hint={contextPreview(context, serverName)}
            checked={draft.attach}
            disabled={busy}
            onChange={(attach) => update({ attach })}
          />

          <StatusText status={status} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button type="button" onClick={close} disabled={busy} sx={secondaryButtonSx}>Отмена</Button>
            <Button
              type="submit"
              disabled={busy}
              sx={primaryButtonSx}
              startIcon={busy ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
            >
              Отправить
            </Button>
          </Box>
          <Typography sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR }}>
            Сообщение получат администраторы Pro Resto. Пароли и данные отчётов не отправляются.
          </Typography>
        </Box>
      )}
    </ResponsiveDialog>
  );
}
